import { useMemo, useState } from 'react'
import { Building2, MapPin, UserRound, CheckCircle2, XCircle, FileCheck2 } from 'lucide-react'
import { formatDateTimeBR } from '@/utils/masks'

export interface ProtocoloTratado {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  status: string
  secretariaId?: string
  subLocalId?: string
  analisadoPorNome?: string
  analisadoEm?: string
}

interface TratadosPorGrupoProps {
  tratados: ProtocoloTratado[]
  secretariaNomeMap: Record<string, string>
  subLocalNomeMap: Record<string, string>
  loading: boolean
}

type Aba = 'setor' | 'sublocal' | 'funcionario'

interface Grupo {
  chave: string
  titulo: string
  subtitulo?: string
  total: number
  aprovados: number
  reprovados: number
  ultimo?: { quem: string, quando: string }
}

const ABAS: { id: Aba, label: string, Icon: typeof Building2 }[] = [
  { id: 'setor', label: 'Por setor', Icon: Building2 },
  { id: 'sublocal', label: 'Por sub local', Icon: MapPin },
  { id: 'funcionario', label: 'Por funcionário', Icon: UserRound },
]

export default function TratadosPorGrupo({ tratados, secretariaNomeMap, subLocalNomeMap, loading }: TratadosPorGrupoProps) {
  const [aba, setAba] = useState<Aba>('setor')

  const grupos = useMemo<Grupo[]>(() => {
    const mapa: Record<string, Grupo> = {}

    const chaveDe = (p: ProtocoloTratado) => {
      if (aba === 'setor') return p.secretariaId || 'sem-setor'
      if (aba === 'sublocal') return p.subLocalId || 'sem-sublocal'
      return (p.analisadoPorNome || 'nao-informado').trim()
    }

    const tituloDe = (p: ProtocoloTratado) => {
      if (aba === 'setor') return secretariaNomeMap[p.secretariaId || ''] || 'Sem secretaria'
      if (aba === 'sublocal') return subLocalNomeMap[p.subLocalId || ''] || 'Sem sub local'
      return p.analisadoPorNome || 'Responsável não informado'
    }

    tratados.forEach((p) => {
      const chave = chaveDe(p)
      if (!mapa[chave]) {
        mapa[chave] = {
          chave,
          titulo: tituloDe(p),
          subtitulo: aba === 'funcionario'
            ? `${secretariaNomeMap[p.secretariaId || ''] || 'Sem secretaria'}`
            : undefined,
          total: 0,
          aprovados: 0,
          reprovados: 0,
        }
      }
      const g = mapa[chave]
      g.total += 1
      if (p.status.startsWith('APROVADO')) g.aprovados += 1
      if (p.status === 'REPROVADO') g.reprovados += 1

      const quando = p.analisadoEm || ''
      if (quando && (!g.ultimo || quando > g.ultimo.quando)) {
        g.ultimo = { quem: p.nomeCompleto || '—', quando }
      }
    })

    return Object.values(mapa).sort((a, b) => b.total - a.total)
  }, [tratados, aba, secretariaNomeMap, subLocalNomeMap])

  const maior = grupos[0]?.total || 0
  const totalGeral = tratados.length
  const totalAprovados = tratados.filter(p => p.status.startsWith('APROVADO')).length
  const totalReprovados = tratados.filter(p => p.status === 'REPROVADO').length

  return (
    <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[14.5px] font-semibold text-[#12141A]">Histórico de requerimentos tratados</h2>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">Distribuição das decisões por âmbito</p>
        </div>
        <div className="grid grid-cols-3 gap-0.5 p-0.5 rounded-lg bg-[#F7F8FA] border border-[#E7E9EC] w-full sm:w-auto sm:inline-flex sm:shrink-0">
          {ABAS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setAba(id)}
              className={`inline-flex items-center justify-center gap-1.5 px-2 py-2 sm:py-1.5 rounded-md text-[11px] sm:text-[11.5px] font-medium transition-colors ${aba === id ? 'bg-white text-[#12141A] shadow-sm border border-[#E7E9EC]' : 'text-[#6B7280] hover:text-[#12141A]'}`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.9} />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
        <div className="rounded-xl bg-[#F7F8FA] px-3 py-2.5">
          <p className="text-[11px] text-[#9CA3AF]">Tratados</p>
          <p className="text-[16px] font-semibold text-[#12141A] tabular-nums">{totalGeral}</p>
        </div>
        <div className="rounded-xl bg-[#F2F9F4] px-3 py-2.5">
          <p className="text-[11px] text-[#6B7280]">Aprovados</p>
          <p className="text-[16px] font-semibold text-[#1E8A45] tabular-nums">{totalAprovados}</p>
        </div>
        <div className="rounded-xl bg-[#FDF3F3] px-3 py-2.5">
          <p className="text-[11px] text-[#6B7280]">Reprovados</p>
          <p className="text-[16px] font-semibold text-[#B91C1C] tabular-nums">{totalReprovados}</p>
        </div>
      </div>

      {loading
        ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-[#F7F8FA] animate-pulse" />)}
            </div>
          )
        : grupos.length === 0
          ? (
              <div className="py-10 flex flex-col items-center justify-center text-center">
                <div className="w-11 h-11 rounded-xl bg-[#F1F2F4] flex items-center justify-center mb-3">
                  <FileCheck2 className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.75} />
                </div>
                <p className="text-[13px] text-[#9CA3AF]">Ainda não há requerimentos tratados.</p>
              </div>
            )
          : (
              <div className="flex flex-col gap-2.5 max-h-[320px] overflow-y-auto pr-1">
                {grupos.map((g) => {
                  const pct = maior > 0 ? Math.round((g.total / maior) * 100) : 0
                  return (
                    <div key={g.chave} className="rounded-xl border border-[#F1F2F4] px-3 sm:px-3.5 py-2.5 hover:border-[#DCE3F0] transition-colors">
                      <div className="flex items-center justify-between gap-2 sm:gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {aba === 'setor' && <Building2 className="w-3.5 h-3.5 text-[#1E4FBF] shrink-0" strokeWidth={1.9} />}
                          {aba === 'sublocal' && <MapPin className="w-3.5 h-3.5 text-[#D9622B] shrink-0" strokeWidth={1.9} />}
                          {aba === 'funcionario' && (
                            <span className="w-6 h-6 rounded-md bg-[#EEF3FE] text-[#1E4FBF] text-[10.5px] font-semibold flex items-center justify-center shrink-0">
                              {g.titulo.charAt(0).toUpperCase()}
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-[#12141A] truncate">{g.titulo}</p>
                            {g.subtitulo && <p className="text-[11px] text-[#9CA3AF] truncate">{g.subtitulo}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
                          {g.aprovados > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1E8A45]">
                              <CheckCircle2 className="w-3 h-3" strokeWidth={2.2} />
                              {g.aprovados}
                            </span>
                          )}
                          {g.reprovados > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#B91C1C]">
                              <XCircle className="w-3 h-3" strokeWidth={2.2} />
                              {g.reprovados}
                            </span>
                          )}
                          <span className="text-[12.5px] font-semibold text-[#12141A] tabular-nums">{g.total}</span>
                        </div>
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-[#F1F2F4] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${aba === 'sublocal' ? 'bg-[#D9622B]' : 'bg-[#1E4FBF]'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {g.ultimo && (
                        <p className="text-[11px] text-[#9CA3AF] mt-1.5 truncate">
                          Último:
                          {' '}
                          {g.ultimo.quem}
                          {' '}
                          • {formatDateTimeBR(g.ultimo.quando)}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
    </div>
  )
}
